/**
 * AnimalPassword Component
 *
 * Kid-friendly password entry using drag-and-drop animals.
 * Students drag animals into 5 boxes to match the code shown by the teacher.
 *
 * Supports two validation modes:
 * - Local validation: When correctCode is provided, validates locally before submitting
 * - Server validation: When correctCode is not provided, submits code directly for server validation
 */

import { useState, useCallback, useEffect, memo } from 'react';
import { DndProvider } from '../DragDrop';
import { AnimalTile } from './AnimalTile';
import { AnimalDropBox } from './AnimalDropBox';
import {
  ANIMALS,
  ANIMAL_CODE_LENGTH,
  type AnimalType,
  type AnimalPasswordProps,
} from './types';
import { validateAnimalCode } from './utils';

/**
 * AnimalPassword - Main component for student password entry
 * Supports both drag-and-drop AND tap-and-tap input modes
 */
export const AnimalPassword = memo(function AnimalPassword({
  onSubmit,
  correctCode,
  onIncorrect,
  disabled = false,
  size = 'lg',
  className = '',
  testId = 'animal-password',
}: AnimalPasswordProps) {
  // State for placed animals (5 boxes)
  const [placedAnimals, setPlacedAnimals] = useState<(AnimalType | null)[]>(
    () => Array(ANIMAL_CODE_LENGTH).fill(null)
  );

  // State for tap-and-tap selection
  const [selectedAnimal, setSelectedAnimal] = useState<AnimalType | null>(null);

  // State for feedback
  const [showError, setShowError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if all boxes are filled
  const allFilled = placedAnimals.every((a) => a !== null);

  // Auto-submit when all boxes are filled
  useEffect(() => {
    // Don't submit if: not all filled, already submitting, or showing error
    if (!allFilled || isSubmitting || showError) {
      return;
    }

    // Small delay for visual feedback
    const timer = setTimeout(async () => {
      const enteredCode = placedAnimals as AnimalType[];

      // If correctCode is provided, validate locally first
      if (correctCode) {
        const isCorrect = validateAnimalCode(enteredCode, correctCode);
        if (isCorrect) {
          setIsSubmitting(true);
          try {
            await onSubmit(enteredCode);
          } finally {
            setIsSubmitting(false);
          }
        } else {
          setShowError(true);
          onIncorrect?.();
          // Clear after showing error
          setTimeout(() => {
            setPlacedAnimals(Array(ANIMAL_CODE_LENGTH).fill(null));
            setShowError(false);
          }, 1000);
        }
      } else {
        // No local validation - submit for server-side validation
        setIsSubmitting(true);
        try {
          await onSubmit(enteredCode);
        } catch {
          // If submission fails, show error and reset
          setShowError(true);
          onIncorrect?.();
          setTimeout(() => {
            setPlacedAnimals(Array(ANIMAL_CODE_LENGTH).fill(null));
            setShowError(false);
          }, 1000);
        } finally {
          setIsSubmitting(false);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [allFilled, placedAnimals, correctCode, onSubmit, onIncorrect, isSubmitting, showError]);

  // Handle animal drop
  const handleDrop = useCallback((index: number, animal: AnimalType) => {
    setPlacedAnimals((prev) => {
      const newAnimals = [...prev];
      newAnimals[index] = animal;
      return newAnimals;
    });
  }, []);

  // Handle click to remove animal
  const handleRemove = useCallback((index: number) => {
    setPlacedAnimals((prev) => {
      const newAnimals = [...prev];
      newAnimals[index] = null;
      return newAnimals;
    });
  }, []);

  // Reset all boxes
  const handleReset = useCallback(() => {
    setPlacedAnimals(Array(ANIMAL_CODE_LENGTH).fill(null));
    setSelectedAnimal(null);
    setShowError(false);
  }, []);

  // Handle animal tile click for tap-and-tap mode
  const handleAnimalTileClick = useCallback((animal: AnimalType) => {
    if (selectedAnimal === animal) {
      // Deselect if same animal tapped
      setSelectedAnimal(null);
    } else {
      // Select new animal
      setSelectedAnimal(animal);
    }
  }, [selectedAnimal]);

  // Handle placing selected animal in a box
  const handlePlaceSelected = useCallback((index: number) => {
    if (selectedAnimal && placedAnimals[index] === null) {
      handleDrop(index, selectedAnimal);
      setSelectedAnimal(null);
    }
  }, [selectedAnimal, placedAnimals, handleDrop]);

  // Size-specific classes
  const gapClass = size === 'sm' ? 'gap-2' : size === 'md' ? 'gap-2' : 'gap-3';
  const containerGap = size === 'sm' ? 'gap-3' : size === 'md' ? 'gap-4' : 'gap-6';
  const textSize = size === 'sm' ? 'text-sm' : size === 'md' ? 'text-base' : 'text-lg';
  const paddingClass = size === 'sm' ? 'p-2' : size === 'md' ? 'p-3' : 'p-4';
  const maxWidthClass = size === 'sm' ? 'max-w-xs' : size === 'md' ? 'max-w-sm' : 'max-w-md';

  return (
    <DndProvider>
      <div
        className={`flex flex-col items-center ${containerGap} ${className}`}
        data-testid={testId}
      >
        {/* Instructions */}
        <p className={`${textSize} text-neutral-600 text-center`}>
          Tap or drag the animals to match what your teacher shows you
        </p>

        {/* Drop boxes */}
        <div
          className={`
            flex ${gapClass} ${paddingClass} rounded-2xl bg-neutral-50 border-2
            ${showError ? 'border-red-400 animate-shake' : 'border-neutral-200'}
            transition-colors duration-200
          `}
          data-testid={`${testId}-boxes`}
        >
          {placedAnimals.map((animal, index) => (
            <AnimalDropBox
              key={index}
              index={index}
              animal={animal}
              dropEnabled={!disabled && !showError && !isSubmitting}
              onDrop={handleDrop}
              onClick={handleRemove}
              selectedAnimal={selectedAnimal}
              onPlaceSelected={handlePlaceSelected}
              size={size}
              testId={`${testId}-box-${index}`}
            />
          ))}
        </div>

        {/* Error message */}
        {showError && (
          <p
            className="text-red-500 font-medium animate-bounce"
            data-testid={`${testId}-error`}
          >
            Try again!
          </p>
        )}

        {/* Loading indicator */}
        {isSubmitting && (
          <p
            className="text-blue-500 font-medium"
            data-testid={`${testId}-loading`}
          >
            Checking...
          </p>
        )}

        {/* Animal tiles to drag or tap */}
        <div
          className={`flex flex-wrap justify-center ${gapClass} ${maxWidthClass}`}
          data-testid={`${testId}-tiles`}
        >
          {ANIMALS.map((animal) => (
            <AnimalTile
              key={animal.type}
              animal={animal.type}
              draggable={!disabled && !showError && !isSubmitting}
              disabled={disabled || showError || isSubmitting}
              isSelected={selectedAnimal === animal.type}
              onClick={handleAnimalTileClick}
              size={size}
              testId={`${testId}-tile-${animal.type}`}
            />
          ))}
        </div>

        {/* Reset button */}
        <button
          type="button"
          onClick={handleReset}
          disabled={disabled || isSubmitting || placedAnimals.every((a) => a === null)}
          className={`
            px-4 py-1.5 rounded-lg text-sm font-medium
            bg-neutral-200 text-neutral-700
            hover:bg-neutral-300
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors duration-150
          `}
          data-testid={`${testId}-reset`}
        >
          Start Over
        </button>
      </div>
    </DndProvider>
  );
});

AnimalPassword.displayName = 'AnimalPassword';

export default AnimalPassword;
